import type {
  AssignableModelFields,
  GetModelConfig,
  List,
  Model,
  ModelConstructor,
  ModelFields,
} from "@mixitone/oom";
import Debug from "debug";
import {
  DEFAULT_SCAN_MAX_IDS,
  DEFAULT_SCAN_MAX_ROWS,
  DEFAULT_SCAN_OUTPUT,
  applyResourceQueryLimit,
  applyResourceFilters,
  DEFAULT_QUERY_RESOURCE_LIMIT,
  MAX_QUERY_RESOURCE_LIMIT,
  MAX_SCAN_IDS,
  MAX_SCAN_ROWS,
  resolveScanMaxIds,
  resolveScanMaxRows,
} from "./agentResourceQuery";
import {
  type AnalyzeResourceBatchesParams,
  type ResourceBatchAnalysisResult,
  runResourceBatchAnalysis,
} from "./agentResourceBatchAnalysis";
import { oneShotComplete } from "./OneShotCompleteService";
import BaseService from "./BaseService";

export {
  DEFAULT_QUERY_RESOURCE_LIMIT,
  DEFAULT_SCAN_MAX_IDS,
  DEFAULT_SCAN_MAX_ROWS,
  DEFAULT_SCAN_OUTPUT,
  MAX_QUERY_RESOURCE_LIMIT,
  MAX_SCAN_IDS,
  MAX_SCAN_ROWS,
  compactToolResultForTransport,
} from "./agentResourceQuery";

const debug = Debug("mixitone:AgentToolsService");

function getResourceFilterFields(modelClass: ModelConstructor<any>): Record<string, unknown> {
  const fields: Record<string, unknown> = { id: {}, ...modelClass.config.fields };
  const availableFields =
    modelClass.modelDescription?.availableFields ?? Object.keys(modelClass.config.fields);
  for (const field of availableFields) {
    if (!(field in fields)) {
      fields[field] = {};
    }
  }
  return fields;
}

/** Fast model for content-composition tool groups (compose_landing_page, compose_email_template). */
export const FAST_COMPOSE_MODEL = "google/gemini-3.1-flash-lite";

interface FieldDefinition {
  required?: boolean;
  type?: string;
}

/** Optional context passed when loading a resource list for query/scan/analysis. */
export interface ResourceListOptions {
  fields?: string[];
  filter?: Record<string, unknown>;
}

export interface ResourceDefinition<M extends Model<any>> {
  name: string;
  modelClass: ModelConstructor<M>;
  list: (options?: ResourceListOptions) => List<M> | Promise<List<M>>;
  fields?: { [index: string]: FieldDefinition };
  add?: (data: AssignableModelFields<GetModelConfig<M>> & Record<string, any>) => Promise<M>;
  update?: (id: string, data: Partial<ModelFields<GetModelConfig<M>>>) => Promise<M>;
  context?: () => string;
}

export function resource<M extends Model<any>>(
  name: string,
  modelClass: ModelConstructor<M>,
  definition: Omit<ResourceDefinition<M>, "name" | "modelClass">,
): ResourceDefinition<M> {
  return {
    name,
    modelClass,
    ...definition,
  };
}

export interface ToolDefinition {
  name: string;
  description: string;
  /**
   * Per-parameter type description (becomes `{ type: "string", description }` in OpenRouter JSON
   * Schema). Use for simple scalar args. For complex / nested args, pass a JSON Schema fragment
   * via `parametersSchema` instead.
   */
  parameters: Record<string, string>;
  /**
   * Optional, full JSON Schema `properties` map. When present it is used verbatim and `parameters`
   * is ignored for schema generation (the descriptions in `parameters` may still document the
   * shape for human readers). Use this when a tool argument is an array of objects, a nested
   * object, an enum, etc., and you want the LLM to receive a precise schema.
   */
  parametersSchema?: Record<string, unknown>;
  /** Names of required parameters (matched against keys in either `parameters` or `parametersSchema`). */
  requiredParameters?: string[];
  execute(parameters: Record<string, any>): Promise<any>;
  group?: string;
  hidden?: boolean;
}

export interface ToolQuery {
  type: "tool";
  tool: string;
  parameters: Record<string, any>;
}

export interface ResourceQuery<M extends Model<any>> {
  type: "resource";
  resource: string;
  fields: string[];
  filter?: { [P in keyof M]?: M[P] };
  limit?: number;
  relationships?: Record<string, string[]>;
}

export type ResourceScanOutput = "count" | "ids" | "ids_and_fields";

export interface ResourceScanQuery<M extends Model<any>> {
  type: "resource_scan";
  resource: string;
  filter?: { [P in keyof M]?: M[P] };
  output?: ResourceScanOutput;
  fields?: string[];
  relationships?: Record<string, string[]>;
  maxIds?: number;
  maxRows?: number;
}

export interface ResourceScanResult {
  type: "resource_scan";
  resource: string;
  output: ResourceScanOutput;
  scannedCount: number;
  matchedCount: number;
  resultCount: number;
  ids?: string[];
  rows?: Array<Record<string, unknown>>;
  truncatedIds?: boolean;
  truncatedRows?: boolean;
  warnings?: string[];
}

export interface AddResourceQuery {
  type: "add_resource";
  resource: string;
  fields: Record<string, any>;
}

export interface UpdateResourceQuery {
  type: "update_resource";
  resource: string;
  id: string;
  fields: Record<string, any>;
}

export interface ToolGroupDefinition {
  name: string;
  description: string;
  tools: Record<string, ToolDefinition>;
  hidden?: boolean;
}

interface ContextEntry {
  controller: ToolContext;
  context?: () => string;
  definitions?: Record<string, ResourceDefinition<any>>;
  tools?: Record<string, ToolDefinition>;
  toolGroups?: Record<string, ToolGroupDefinition>;
  ignorePreviousContext?: boolean;
  suggestedExamples?: string[];
  preferredModel?: string;
}

interface State {
  contexts: ContextEntry[];
  resources: Map<string, ResourceDefinition<any>>;
  tools: Map<string, ToolDefinition>;
  toolGroups: Map<string, ToolGroupDefinition>;
  fetchedToolGroups: Set<string>; // Track tool groups that have been fetched via get_tool_info
  contextsVersion: number;
}

type ToolContext = string | object;

function contextLabel(controller: ToolContext): string {
  if (typeof controller === "string") return controller;
  return controller?.constructor?.name || "context";
}

export class AgentToolsService extends BaseService<State> {
  private static instance: AgentToolsService | null = null;

  get initialState(): State {
    return {
      contexts: [],
      resources: new Map(),
      tools: new Map(),
      toolGroups: new Map(),
      fetchedToolGroups: new Set(),
      contextsVersion: 0,
    };
  }

  /**
   * Get the singleton instance of AgentToolsService
   */
  static getInstance(): AgentToolsService {
    if (!AgentToolsService.instance) {
      AgentToolsService.instance = new AgentToolsService();
    }
    return AgentToolsService.instance;
  }

  /**
   * Clear the singleton instance (useful for testing)
   */
  static clearInstance(): void {
    AgentToolsService.instance = null;
  }

  private rebuild() {
    // Rebuild the global resources, tools, and toolGroups maps from contexts.
    // Later registrations replace earlier ones. A repeated resource name is a bug:
    // page controllers used to overwrite the account-wide clubs list this way.
    this.state.resources.clear();
    this.state.tools.clear();
    this.state.toolGroups.clear();
    this.state.contextsVersion += 1;

    const resourceOwners = new Map<string, string>();

    for (const entry of this.state.contexts) {
      if (entry.definitions) {
        for (const [name, resource] of Object.entries(entry.definitions)) {
          const owner = contextLabel(entry.controller);
          const earlier = resourceOwners.get(name);
          if (earlier) {
            this.warnDuplicateResource(name, earlier, owner);
          }
          resourceOwners.set(name, owner);
          this.state.resources.set(name, resource);
        }
      }
      if (entry.tools) {
        for (const [name, tool] of Object.entries(entry.tools)) {
          this.state.tools.set(name, tool);
        }
      }
      if (entry.toolGroups) {
        for (const [name, group] of Object.entries(entry.toolGroups)) {
          this.state.toolGroups.set(name, group);
          // Register tools from groups with group metadata
          for (const [toolName, tool] of Object.entries(group.tools)) {
            const toolWithGroup = {
              ...tool,
              group: group.name,
              hidden: group.hidden ?? false,
            };
            this.state.tools.set(toolName, toolWithGroup);
          }
        }
      }
    }
  }

  private warnDuplicateResource(name: string, earlier: string, later: string): void {
    if (!import.meta.env.DEV) return;
    console.warn(
      `AgentToolsService: resource "${name}" is registered by both ${earlier} and ${later}. The later registration replaces the earlier one.`,
    );
  }

  /**
   * Drop every registered context. Tests use this on the shared singleton,
   * which stays the instance controllers imported at module load.
   */
  clearContexts(): void {
    this.state.contexts = [];
    this.state.fetchedToolGroups.clear();
    this.rebuild();
  }

  /**
   * Add a resource definition to the service
   * @param definition The resource definition to add
   * @return A function to remove the resource
   */
  addResources(
    controller: ToolContext,
    {
      context,
      definitions,
      tools,
      toolGroups,
      ignorePreviousContext,
      suggestedExamples,
      preferredModel,
    }: AddResourcesOptions,
  ): () => void {
    this.state.contexts.push({
      controller,
      context,
      definitions,
      tools,
      toolGroups,
      ignorePreviousContext,
      suggestedExamples,
      preferredModel,
    });
    this.rebuild();

    return () => this.removeResources(controller);
  }

  removeResources(controller: ToolContext): void {
    this.state.contexts = this.state.contexts.filter((entry) => entry.controller !== controller);
    this.rebuild();
  }

  /**
   * Get a resource definition by name
   * @param name The name of the resource
   * @returns The resource definition or undefined if not found
   */
  getResource<M extends Model<any>>(name: string): ResourceDefinition<M> | undefined {
    return this.state.resources.get(name);
  }

  /**
   * Get all available resource definitions
   * @returns Array of all resource definitions
   */
  getAllResources(): ResourceDefinition<any>[] {
    return Array.from(this.state.resources.values());
  }

  getTool(name: string): ToolDefinition | undefined {
    return this.state.tools.get(name);
  }

  getAllTools(): ToolDefinition[] {
    return Array.from(this.state.tools.values());
  }

  /**
   * Get tool group definition by name
   * @param groupName The name of the tool group
   * @returns The tool group definition or undefined if not found
   */
  getToolGroup(groupName: string): ToolGroupDefinition | undefined {
    return this.state.toolGroups.get(groupName);
  }

  /**
   * Get all tool groups
   * @returns Array of all tool group definitions
   */
  getAllToolGroups(): ToolGroupDefinition[] {
    return Array.from(this.state.toolGroups.values());
  }

  /**
   * Get tool definitions for a specific group in OpenRouter format
   * @param groupName The name of the tool group
   * @returns Array of tool definitions in OpenRouter format
   */
  getToolGroupInfo(groupName: string): Array<{
    type: "function";
    function: {
      name: string;
      description: string;
      parameters: {
        type: "object";
        properties: Record<string, any>;
        required?: string[];
      };
    };
  }> {
    const group = this.getToolGroup(groupName);
    if (!group) {
      throw new Error(`Unknown tool group: ${groupName}`);
    }

    const tools: Array<{
      type: "function";
      function: {
        name: string;
        description: string;
        parameters: {
          type: "object";
          properties: Record<string, any>;
          required?: string[];
        };
      };
    }> = [];

    for (const tool of Object.values(group.tools)) {
      tools.push(this.toolToOpenRouterFormat(tool));
    }

    return tools;
  }

  getContext(): string {
    // Find the last entry with ignorePreviousContext: true
    let startIndex = 0;
    for (let i = this.state.contexts.length - 1; i >= 0; i--) {
      if (this.state.contexts[i].ignorePreviousContext) {
        startIndex = i;
        break;
      }
    }

    // Only include contexts from startIndex forward
    const contextParts: string[] = [];

    for (const entry of this.state.contexts.slice(startIndex)) {
      // Add controller-level context if provided
      if (entry.context) {
        contextParts.push(entry.context());
      }

      // Add resource-level contexts if provided
      if (entry.definitions) {
        for (const resource of Object.values(entry.definitions)) {
          if (resource.context) {
            contextParts.push(resource.context());
          }
        }
      }
    }

    return contextParts.join("\n");
  }

  /**
   * Walk active contexts and return the last non-empty preferredModel.
   * Mirrors getContext() resolution: last wins.
   */
  getPreferredModel(): string | undefined {
    let model: string | undefined;
    for (const entry of this.state.contexts) {
      if (entry.preferredModel) model = entry.preferredModel;
    }
    return model;
  }

  /**
   * Get suggested chat examples from all active controller contexts (same order as getContext).
   * Nested controllers contribute their examples in order, so e.g. FundingController then
   * a child controller yields both sets of examples.
   */
  getSuggestedExamples(): string[] {
    let startIndex = 0;
    for (let i = this.state.contexts.length - 1; i >= 0; i--) {
      if (this.state.contexts[i].ignorePreviousContext) {
        startIndex = i;
        break;
      }
    }

    const examples: string[] = [];
    for (const entry of this.state.contexts.slice(startIndex)) {
      if (entry.suggestedExamples?.length) {
        examples.push(...entry.suggestedExamples);
      }
    }
    return examples;
  }

  async executeQuery(metadata: any): Promise<any> {
    if (!metadata) return null;
    if (!("type" in metadata)) return null;

    switch (metadata.type) {
      case "tool":
        return this.executeToolQuery(metadata);
      case "resource":
        return this.executeResourceQuery(metadata);
      case "resource_scan":
        return this.executeResourceScan(metadata);
      case "add_resource":
        return this.executeAddResourceQuery(metadata);
      case "update_resource":
        return this.executeUpdateResourceQuery(metadata);
      default:
        return null;
    }
  }

  /**
   * Execute a resource query
   * @param resourceName The name of the resource to query
   * @param filter Optional filter criteria
   * @param limit Optional limit for results
   * @returns Promise resolving to a List of matching records
   */
  async executeResourceQuery<M extends Model<any>>(resourceQuery: ResourceQuery<M>): Promise<any> {
    const { resource: resourceName, filter, limit, fields, relationships } = resourceQuery;

    const resource = this.getResource<M>(resourceName);
    if (!resource) {
      throw new Error(`Unknown resource: ${resourceName}`);
    }

    debug("Executing resource query", { resourceName, filter, limit });

    const list = await resource.list({ fields, filter: filter as Record<string, unknown> | undefined });
    const { items: filteredList, warnings } = applyResourceFilters(list, filter as Record<string, any>, {
      mode: "query",
      resourceName,
      fields: getResourceFilterFields(resource.modelClass),
    });
    for (const warning of warnings) {
      debug(warning);
    }

    const {
      items,
      totalCount,
      truncated,
      limit: appliedLimit,
    } = applyResourceQueryLimit(filteredList, limit);

    const response: Record<string, unknown> = {
      type: "resource",
      resource: resourceName,
      result: mapSelectedFields(items, fields, relationships || {}),
      totalCount,
      limit: appliedLimit,
    };
    if (truncated) {
      response.truncated = true;
    }
    return response;
  }

  async executeResourceScan<M extends Model<any>>(
    resourceScan: ResourceScanQuery<M>,
  ): Promise<ResourceScanResult> {
    const {
      resource: resourceName,
      filter,
      output = DEFAULT_SCAN_OUTPUT,
      fields = [],
      relationships,
      maxIds,
      maxRows,
    } = resourceScan;

    const resource = this.getResource<M>(resourceName);
    if (!resource) {
      throw new Error(`Unknown resource: ${resourceName}`);
    }

    if (!["count", "ids", "ids_and_fields"].includes(output)) {
      throw new Error(`Unsupported scan_resource output: ${output}`);
    }

    debug("Executing resource scan", { resourceName, filter, output, maxIds, maxRows });

    const list = await resource.list({ fields, filter: filter as Record<string, unknown> | undefined });
    const { items: filteredList, warnings } = applyResourceFilters(list, filter as Record<string, any>, {
      mode: "scan",
      resourceName,
      fields: getResourceFilterFields(resource.modelClass),
    });

    const response: ResourceScanResult = {
      type: "resource_scan",
      resource: resourceName,
      output,
      scannedCount: list.length,
      matchedCount: filteredList.length,
      resultCount: filteredList.length,
    };

    if (warnings.length > 0) {
      response.warnings = warnings;
    }

    if (output === "ids") {
      const appliedMaxIds = resolveScanMaxIds(maxIds);
      const ids = filteredList.slice(0, appliedMaxIds).map((item) => String(item.id));
      response.ids = ids;
      response.resultCount = ids.length;
      if (filteredList.length > ids.length) {
        response.truncatedIds = true;
      }
      return response;
    }

    if (output === "ids_and_fields") {
      const appliedMaxRows = resolveScanMaxRows(maxRows);
      const selectedFields = fields.includes("id") ? fields : ["id", ...fields];
      const rows = mapSelectedFields(
        filteredList.slice(0, appliedMaxRows),
        selectedFields,
        relationships || {},
      );
      response.rows = rows;
      response.resultCount = rows.length;
      if (filteredList.length > rows.length) {
        response.truncatedRows = true;
      }
      return response;
    }

    return response;
  }

  async executeResourceBatchAnalysis(
    params: AnalyzeResourceBatchesParams,
    options?: {
      oneShotComplete?: typeof oneShotComplete;
    },
  ): Promise<ResourceBatchAnalysisResult> {
    const {
      resource: resourceName,
      filter,
      fields,
      relationships,
      question,
      batchSize,
      maxBatches,
      maxMatchedIds,
      maxReasons,
    } = params;

    if (!resourceName) {
      throw new Error(`"resource" is required for analyze_resource_batches`);
    }
    if (!question || typeof question !== "string") {
      throw new Error(`"question" is required for analyze_resource_batches`);
    }
    if (!Array.isArray(fields) || fields.length === 0) {
      throw new Error(`"fields" is required for analyze_resource_batches`);
    }

    const resource = this.getResource(resourceName);
    if (!resource) {
      throw new Error(`Unknown resource: ${resourceName}`);
    }

    debug("Executing resource batch analysis", { resourceName, filter, fields, question });

    const list = await resource.list({ fields, filter: filter as Record<string, unknown> | undefined });
    const availableFields = getResourceFilterFields(resource.modelClass);
    const fieldWarnings: string[] = [];
    for (const field of fields) {
      if (!(field in availableFields)) {
        fieldWarnings.push(`Field "${field}" does not exist in resource "${resourceName}".`);
      }
    }

    const { items: filteredList, warnings: filterWarnings } = applyResourceFilters(
      list,
      filter as Record<string, any>,
      {
        mode: "scan",
        resourceName,
        fields: availableFields,
      },
    );

    const selectedFields = fields.includes("id") ? fields : ["id", ...fields];
    const rows = mapSelectedFields(filteredList, selectedFields, relationships || {}) as Array<
      Record<string, unknown> & { id: string }
    >;

    return runResourceBatchAnalysis({
      params: {
        resource: resourceName,
        question,
        filter,
        fields: selectedFields,
        relationships,
        batchSize,
        maxBatches,
        maxMatchedIds,
        maxReasons,
      },
      rows,
      scannedCount: list.length,
      filterWarnings: [...filterWarnings, ...fieldWarnings],
      oneShotComplete: options?.oneShotComplete ?? oneShotComplete,
    });
  }

  async executeAddResourceQuery(addResourceQuery: AddResourceQuery) {
    if (!addResourceQuery.resource) throw new Error(`"name" is required`);
    const resource = this.getResource(addResourceQuery.resource);
    if (!resource) throw new Error(`Unknown resource ${addResourceQuery.resource}`);
    if (!resource.add) throw new Error(`Resource ${addResourceQuery.resource} does not support adding`);
    const fields = addResourceQuery.fields;
    if (!fields || typeof fields !== "object")
      throw new Error(`"fields" is required to be an object of field names and values`);

    const missingRequiredFields = resource.fields
      ? Object.entries(resource.fields)
          .filter(([fieldName, fieldDef]) => fieldDef?.required === true && !(fieldName in fields))
          .map(([fieldName]) => fieldName)
      : [];

    if (missingRequiredFields.length > 0) {
      throw new Error(`Missing required fields: ${missingRequiredFields.join(", ")}`);
    }

    const result = await resource.add(fields);

    return {
      type: "add_resource",
      resource: addResourceQuery.resource,
      result: mapSelectedFields([result], ["id", ...Object.keys(resource.modelClass.config.fields)], {})[0],
    };
  }

  async executeUpdateResourceQuery(updateResourceQuery: UpdateResourceQuery) {
    if (!updateResourceQuery.resource) throw new Error(`"name" is required`);
    if (!updateResourceQuery.id) throw new Error(`"id" is required`);
    const resource = this.getResource(updateResourceQuery.resource);
    if (!resource) throw new Error(`Unknown resource ${updateResourceQuery.resource}`);
    if (!resource.update)
      throw new Error(`Resource ${updateResourceQuery.resource} does not support updating`);
    const fields = updateResourceQuery.fields;
    if (!fields || typeof fields !== "object")
      throw new Error(`"fields" is required to be an object of field names and values`);

    const updatingFields = new Set(Object.keys(fields));
    const availableFields = new Set(Object.keys(resource.fields || {}));
    // Error if there are fields to be udpated that are not available
    const unavailableFields = updatingFields.difference(availableFields);
    if (unavailableFields.size > 0) {
      throw new Error(`Fields ${Array.from(unavailableFields).join(", ")} are not available for updating`);
    }

    const result = await resource.update(updateResourceQuery.id, fields);

    return {
      type: "update_resource",
      resource: updateResourceQuery.resource,
      id: updateResourceQuery.id,
      result: {
        success: true,
        record: mapSelectedFields([result], ["id", ...Object.keys(resource.modelClass.config.fields)], {})[0],
      },
    };
  }

  async executeToolQuery(toolQuery: ToolQuery): Promise<any> {
    const { tool: toolName, parameters } = toolQuery;

    const tool = this.getTool(toolName);
    if (!tool) {
      console.log(this.state.tools);
      throw new Error(`Unknown tool: ${toolName}`);
    }

    debug("Executing tool query", { toolName, parameters });

    return {
      type: "tool",
      tool: toolName,
      result: await tool.execute(parameters),
    };
  }

  /**
   * Execute a tool call from OpenRouter format
   * @param toolName The name of the tool (e.g., "query_resource", "add_resource", "update_resource", or custom tool name)
   * @param parameters The parameters passed to the tool
   * @returns Promise resolving to the tool result
   */
  async executeOpenRouterToolCall(toolName: string, parameters: Record<string, any>): Promise<any> {
    debug("Executing OpenRouter tool call", { toolName, parameters });

    // Handle query_resource
    if (toolName === "query_resource") {
      return this.executeResourceQuery({
        type: "resource",
        resource: parameters.resource,
        fields: parameters.fields,
        filter: parameters.filter,
        relationships: parameters.relationships,
        limit: parameters.limit,
      });
    }

    // Handle scan_resource
    if (toolName === "scan_resource") {
      return this.executeResourceScan({
        type: "resource_scan",
        resource: parameters.resource,
        filter: parameters.filter,
        output: parameters.output,
        fields: parameters.fields,
        relationships: parameters.relationships,
        maxIds: parameters.maxIds,
        maxRows: parameters.maxRows,
      });
    }

    // Handle analyze_resource_batches
    if (toolName === "analyze_resource_batches") {
      return this.executeResourceBatchAnalysis({
        resource: parameters.resource,
        question: parameters.question,
        filter: parameters.filter,
        fields: parameters.fields,
        relationships: parameters.relationships,
        batchSize: parameters.batchSize,
        maxBatches: parameters.maxBatches,
        maxMatchedIds: parameters.maxMatchedIds,
        maxReasons: parameters.maxReasons,
      });
    }

    // Handle add_resource
    if (toolName === "add_resource") {
      const { resource, fields } = parameters;
      if (!resource) {
        throw new Error(`"resource" is required for add_resource`);
      }
      if (!fields || typeof fields !== "object") {
        throw new Error(`"fields" is required to be an object for add_resource`);
      }
      return this.executeAddResourceQuery({
        type: "add_resource",
        resource,
        fields,
      });
    }

    // Handle update_resource
    if (toolName === "update_resource") {
      const { resource, id, fields } = parameters;
      if (!resource) {
        throw new Error(`"resource" is required for update_resource`);
      }
      if (!id) {
        throw new Error(`"id" is required for update_resource`);
      }
      if (!fields || typeof fields !== "object") {
        throw new Error(`"fields" is required to be an object for update_resource`);
      }
      return this.executeUpdateResourceQuery({
        type: "update_resource",
        resource,
        id,
        fields,
      });
    }

    // Handle get_tool_info
    if (toolName === "get_tool_info") {
      const { group } = parameters;
      if (!group || typeof group !== "string") {
        throw new Error(`"group" parameter is required for get_tool_info`);
      }
      const toolDefinitions = this.getToolGroupInfo(group);
      // Mark this group as fetched so tools are included in subsequent requests
      this.state.fetchedToolGroups.add(group);
      return {
        type: "tool",
        tool: toolName,
        result: {
          group,
          tools: toolDefinitions,
          message: `Retrieved ${toolDefinitions.length} tool(s) from group "${group}"`,
        },
      };
    }

    // Handle stub tools (redirect to get_tool_info)
    if (toolName.endsWith("_stub")) {
      const groupName = toolName.replace("_stub", "");
      const group = this.getToolGroup(groupName);
      if (group) {
        return {
          type: "tool",
          tool: toolName,
          result: {
            message: `This is a stub for the "${groupName}" tool group. Use get_tool_info with group="${groupName}" to fetch the actual tool definitions.`,
            group: groupName,
            description: group.description,
          },
        };
      }
    }

    // Handle custom tools
    const tool = this.getTool(toolName);
    if (tool) {
      return {
        type: "tool",
        tool: toolName,
        result: await tool.execute(parameters),
      };
    }

    throw new Error(`Unknown tool: ${toolName}`);
  }

  async getResourceById<M extends Model<any>>(id: string): Promise<M | undefined> {
    const resources = this.getAllResources();
    for (const resource of resources) {
      const list = await resource.list();
      const item = list.findById(id);
      if (item) {
        return item;
      }
    }
    return undefined;
  }

  /**
   * Get resource information formatted for AI consumption
   * @returns Array of resource info objects
   */
  getResourceInfoForAI(): Array<{
    name: string;
    description: string;
    availableFields: string[];
    relationships: Record<string, string>;
  }> {
    return this.getAllResources().map((resource) => {
      const modelClass = resource.modelClass;

      const instructions = {
        name: resource.name,
        description: modelClass.modelDescription?.description || "",
        availableFields: [
          "id",
          ...(modelClass.modelDescription?.availableFields || Object.keys(modelClass.config.fields)),
        ],
        relationships: modelClass.modelDescription?.relationships || {},
      };

      if (resource.add && resource.fields) {
        const fieldNames = Object.keys(resource.fields);
        const requiredFields = Object.entries(resource.fields)
          .filter(([_, r]) => r?.required)
          .map(([n]) => n);

        instructions.description = `${instructions.description}. You can create a new ${resource.name} by calling add_resource with {type: "add_resource", "resource": ${resource.name}, "fields": {
            ${fieldNames.map((n) => `"${n}": ${modelClass.config.fields[n]?.type}`).join(",")}
          }} with fields ${requiredFields.join(",")} being required`;
      }

      if (resource.update && resource.fields) {
        const fieldNames = Object.keys(resource.fields);

        instructions.description = `${instructions.description}. IMPORTANT: To update a ${resource.name}, you MUST first query the resource to find its ID. Never make up or guess IDs. Workflow: 1) Query ${resource.name} with filters to find the record and get its ID, 2) Use that ID to call update_resource. You can update a ${resource.name} by calling update_resource with {type: "update_resource", "resource": ${resource.name}, "id": string id of resource (MUST be obtained from a previous resource query), "fields": {
            ${fieldNames.map((n) => `"${n}": ${modelClass.config.fields[n]?.type}`).join(",")}
          }} all fields are optional for updates.`;
      }

      return instructions;
    });
  }

  getToolInfoForAI(): Array<{
    name: string;
    description: string;
    parameters: Record<string, string>;
  }> {
    return this.getAllTools().map((tool) => ({
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    }));
  }

  /**
   * Convert field type from model config to JSON Schema type
   */
  fieldTypeToJsonSchema(field: any): {
    type: string;
    items?: { type: string };
    enum?: any[];
  } {
    if (!field || !field.type) {
      return { type: "string" };
    }

    switch (field.type) {
      case "string":
        return { type: "string" };
      case "string_array":
        return { type: "array", items: { type: "string" } };
      case "number":
        return { type: "number" };
      case "number_array":
        return { type: "array", items: { type: "number" } };
      case "boolean":
        return { type: "boolean" };
      case "datetime":
        return { type: "string" }; // ISO 8601 string
      case "enum":
        return { type: "string", enum: field.values || [] };
      case "json":
        return { type: "object" };
      default:
        return { type: "string" };
    }
  }

  /**
   * Convert tool definition to OpenRouter tool format
   */
  toolToOpenRouterFormat(tool: ToolDefinition): {
    type: "function";
    function: {
      name: string;
      description: string;
      parameters: {
        type: "object";
        properties: Record<string, any>;
        required?: string[];
      };
    };
  } {
    const properties: Record<string, any> = {};
    const required: string[] = tool.requiredParameters ? [...tool.requiredParameters] : [];

    if (tool.parametersSchema) {
      // Caller supplied a full JSON Schema properties map — pass through as-is.
      for (const [key, value] of Object.entries(tool.parametersSchema)) {
        properties[key] = value;
      }
    } else {
      // Legacy path: every parameter is a string with a free-text description.
      for (const [key, value] of Object.entries(tool.parameters)) {
        const description = typeof value === "string" ? value : String(value);
        if (!tool.requiredParameters && description.match(/\brequired\b/i)) {
          required.push(key);
        }
        properties[key] = { type: "string", description };
      }
    }

    return {
      type: "function",
      function: {
        name: tool.name,
        description: tool.description,
        parameters: {
          type: "object",
          properties,
          ...(required.length > 0 && { required }),
        },
      },
    };
  }

  /**
   * Convert resources to OpenRouter tools
   */
  getToolsForOpenRouter(): Array<{
    type: "function";
    function: {
      name: string;
      description: string;
      parameters: {
        type: "object";
        properties: Record<string, any>;
        required?: string[];
      };
    };
  }> {
    const tools: Array<{
      type: "function";
      function: {
        name: string;
        description: string;
        parameters: {
          type: "object";
          properties: Record<string, any>;
          required?: string[];
        };
      };
    }> = [];

    // Add resource query tool
    const resources = this.getAllResources();
    if (resources.length > 0) {
      const resourceNames = resources.map((r) => r.name);
      // Build descriptions with available fields for each resource
      const resourceDescriptions = resources.map((r) => {
        const modelClass = r.modelClass;
        const availableFields = [
          "id",
          ...(modelClass.modelDescription?.availableFields || Object.keys(modelClass.config.fields)),
        ];
        return `- ${r.name}: ${modelClass.modelDescription?.description || "Resource"} (Available fields: ${availableFields.join(", ")})`;
      });

      tools.push({
        type: "function",
        function: {
          name: "query_resource",
          description: `Query a resource to get data. Use this to look up information about tournaments, players, competitions, registrations, and other data.

Available resources:
${resourceDescriptions.join("\n")}

RESULT LIMITS: Responses are capped (default ${DEFAULT_QUERY_RESOURCE_LIMIT} rows, max ${MAX_QUERY_RESOURCE_LIMIT}). When truncated, the response includes totalCount and truncated: true. For large lists, request only the fields you need (e.g. id, name), set an explicit limit, or run multiple narrower queries. For counts, use a small limit and read totalCount.

IMPORTANT: When filtering, you can ONLY filter on fields listed in "Available fields" for each resource. Filter values must use one of these structures:
- {"fieldName": {"eq": "value"}} for exact matches (case-insensitive for strings)
- {"fieldName": {"partial": "value"}} for partial matches (case-insensitive for strings)
- {"fieldName": "value"} for legacy exact matches (will be converted to eq)

Example:
{
  "resource": "tournament_players",
  "fields": ["id", "name", "email"],
  "limit": 25,
  "filter": {
    "name": {"partial": "Alex"},
    "status": {"eq": "active"}
  }
}`,
          parameters: {
            type: "object",
            properties: {
              resource: {
                type: "string",
                enum: resourceNames,
                description:
                  "The name of the resource to query. Use exact resource names from the available resources list above.",
              },
              fields: {
                type: "array",
                items: { type: "string" },
                description:
                  "Array of field names to include in the response. Use only fields from the 'Available fields' list for this resource.",
              },
              filter: {
                type: "object",
                description: `Optional filter criteria. Filter format: {"fieldName": {"eq": "value"}} for exact matches or {"fieldName": {"partial": "value"}} for partial matches. You can ONLY filter on fields listed in "Available fields" for the selected resource. Example: {"name": {"partial": "Alex"}, "status": {"eq": "active"}}`,
                additionalProperties: true,
              },
              relationships: {
                type: "object",
                description:
                  "Optional related resources to include, with arrays of fields to include for each",
              },
              limit: {
                type: "number",
                description: `Optional row limit (default ${DEFAULT_QUERY_RESOURCE_LIMIT}, max ${MAX_QUERY_RESOURCE_LIMIT}). Use a small limit for list queries; check totalCount when truncated is true.`,
              },
            },
            required: ["resource", "fields"],
          },
        },
      });

      tools.push({
        type: "function",
        function: {
          name: "scan_resource",
          description: `Scan a registered resource deterministically and return compact counts, IDs, or selected fields without paging raw rows. Use this for broad structural questions, date ranges, "how many match X", or "give me IDs for records matching X". Use query_resource for small row inspection.

Available resources:
${resourceDescriptions.join("\n")}

Supported filters:
- {"fieldName": {"eq": "value"}} exact match, case-insensitive for strings
- {"fieldName": {"partial": "value"}} substring match, case-insensitive for strings
- {"fieldName": {"gte": "2026-01-01T00:00:00.000Z", "lt": "2027-01-01T00:00:00.000Z"}} date or numeric ranges
- {"fieldName": {"in": ["active", "pending"]}} membership
- {"fieldName": {"exists": true}} presence`,
          parameters: {
            type: "object",
            properties: {
              resource: {
                type: "string",
                enum: resourceNames,
                description:
                  "The name of the resource to scan. Use exact resource names from the available resources list above.",
              },
              filter: {
                type: "object",
                description:
                  "Optional filter criteria. Supports eq, partial, gte, gt, lte, lt, in, and exists operators.",
                additionalProperties: true,
              },
              output: {
                type: "string",
                enum: ["count", "ids", "ids_and_fields"],
                description: `Output mode. Defaults to ${DEFAULT_SCAN_OUTPUT}. Use count for totals (matchedCount and resultCount), ids for compact matching IDs, or ids_and_fields for compact rows.`,
              },
              fields: {
                type: "array",
                items: { type: "string" },
                description:
                  "Fields to include when output is ids_and_fields. id is always included. Use only fields from the selected resource.",
              },
              relationships: {
                type: "object",
                description:
                  "Optional related resources to include with ids_and_fields, with arrays of fields to include for each.",
              },
              maxIds: {
                type: "number",
                description: `Optional ID return cap for output=ids (default ${DEFAULT_SCAN_MAX_IDS}, max ${MAX_SCAN_IDS}).`,
              },
              maxRows: {
                type: "number",
                description: `Optional row return cap for output=ids_and_fields (default ${DEFAULT_SCAN_MAX_ROWS}, max ${MAX_SCAN_ROWS}).`,
              },
            },
            required: ["resource"],
          },
        },
      });
    }

    // Add generic add_resource tool
    const resourcesWithAdd = resources.filter((r) => r.add && r.fields);
    if (resourcesWithAdd.length > 0) {
      const resourceNames = resourcesWithAdd.map((r) => r.name);
      tools.push({
        type: "function",
        function: {
          name: "add_resource",
          description:
            "Create a new resource. Use this to add new records like tournament players, competitions, registrations, etc.",
          parameters: {
            type: "object",
            properties: {
              resource: {
                type: "string",
                enum: resourceNames,
                description: "The name of the resource to create",
              },
              fields: {
                type: "object",
                description:
                  "An object containing the field names and values for the resource. Field names and types vary by resource - use query_resource first to see available fields.",
                additionalProperties: true,
              },
            },
            required: ["resource", "fields"],
          },
        },
      });
    }

    // Add generic update_resource tool
    const resourcesWithUpdate = resources.filter((r) => r.update && r.fields);
    if (resourcesWithUpdate.length > 0) {
      const resourceNames = resourcesWithUpdate.map((r) => r.name);
      tools.push({
        type: "function",
        function: {
          name: "update_resource",
          description:
            "Update an existing resource. IMPORTANT: You MUST first query the resource to find its ID. Never make up or guess IDs. Workflow: 1) Query the resource with filters to find the record and get its ID, 2) Use that ID to call this function.",
          parameters: {
            type: "object",
            properties: {
              resource: {
                type: "string",
                enum: resourceNames,
                description: "The name of the resource to update",
              },
              id: {
                type: "string",
                description:
                  "The ID of the resource to update. This MUST be obtained from a previous query_resource call. Never make up IDs.",
              },
              fields: {
                type: "object",
                description:
                  "An object containing the field names and values to update. All fields are optional for updates. Field names and types vary by resource - use query_resource first to see available fields.",
                additionalProperties: true,
              },
            },
            required: ["resource", "id", "fields"],
          },
        },
      });
    }

    // Add get_tool_info tool (always available, not hidden)
    const hiddenGroups = this.getAllToolGroups().filter((g) => g.hidden);
    if (hiddenGroups.length > 0) {
      const groupNames = hiddenGroups.map((g) => g.name);
      tools.push({
        type: "function",
        function: {
          name: "get_tool_info",
          description: `IMPORTANT: Get detailed tool definitions for a tool group. Use this to fetch instructions for tools that are not included in the initial context. This is REQUIRED if you need to interact with the browser (fill forms, click buttons, type text, etc.) or run semantic resource batch analysis. Available hidden tool groups: ${groupNames.join(", ")}. To fill out forms or interact with the page, call this with group="browser_tools" first. For judgment-based questions over large resource sets, call this with group="resource_analysis_tools".`,
          parameters: {
            type: "object",
            properties: {
              group: {
                type: "string",
                enum: groupNames,
                description:
                  "The name of the tool group to fetch tool definitions for. Use 'browser_tools' to get tools for interacting with forms and the browser.",
              },
            },
            required: ["group"],
          },
        },
      });
    }

    // Add custom tools (excluding tools from hidden groups, unless they've been fetched)
    for (const tool of this.getAllTools()) {
      // Skip tools that belong to hidden groups, unless the group has been fetched
      if (tool.hidden && tool.group && !this.state.fetchedToolGroups.has(tool.group)) {
        continue;
      }
      tools.push(this.toolToOpenRouterFormat(tool));
    }

    // Add stub tools for hidden groups
    for (const group of hiddenGroups) {
      tools.push({
        type: "function",
        function: {
          name: `${group.name}_stub`,
          description: `${group.description}. IMPORTANT: To use these tools, you MUST first call get_tool_info with group="${group.name}" to fetch the actual tool definitions. This stub is just a placeholder - call get_tool_info to get the real tools.`,
          parameters: {
            type: "object",
            properties: {},
          },
        },
      });
    }

    return tools;
  }
}

// Create a singleton instance for global access
export const agentToolsService = AgentToolsService.getInstance();
export default agentToolsService;

export interface AddResourcesOptions {
  context?: () => string;
  definitions?: Record<string, ResourceDefinition<any>>;
  tools?: Record<string, ToolDefinition>;
  toolGroups?: Record<string, ToolGroupDefinition>;
  ignorePreviousContext?: boolean;
  /** Suggested chat examples for the assistant; nested controllers' examples are combined in order. */
  suggestedExamples?: string[];
  /** Optional OpenRouter model to prefer for interactions in this context (e.g. fast compose model for content generation tools). */
  preferredModel?: string;
}

function mapSelectedFields(items: any[], fields: string[], relationships: Record<string, string[]>) {
  console.log("Mapping selected fields", { items, fields, relationships });
  return items.map((item) => {
    const mapped: any = {};

    for (const field of fields) {
      if (field in item) {
        mapped[field] = item[field];
      }
    }
    for (const [relName, relFields] of Object.entries(relationships || {})) {
      if (item[relName]) {
        if (Array.isArray(item[relName])) {
          if (Array.isArray(relFields)) {
            mapped[relName] = mapSelectedFields(item[relName], relFields, {});
          } else if (typeof relFields === "object") {
            mapped[relName] = mapSelectedFields(item[relName], [], relFields);
          }
        } else {
          if (Array.isArray(relFields)) {
            mapped[relName] = mapSelectedFields([item[relName]], relFields, {})[0];
          } else if (typeof relFields === "object") {
            mapped[relName] = mapSelectedFields([item[relName]], [], relFields)[0];
          }
        }
      }
    }
    return mapped;
  });
}
